// src/pages/Accounting/Reconcile/Session/SessionFilterBar.tsx

import type React from "react";
import { Filter, Lightbulb, Printer, Settings2 } from "lucide-react";
import { PALETTE } from "../constants";
import type { TabFilter } from "./sessionTypes";

interface SessionFilterBarProps {
  activeTab: TabFilter;
  onTabChange: (tab: TabFilter) => void;
  counts: Record<TabFilter, number>;
  statementDateChipLabel: string;
  onToggleFilterPopover: () => void;
  filterPopoverOpen: boolean;
  onClearChip: () => void;
  onClearAllFilters: () => void;
  onOpenHowItWorks: () => void;
  onPrint: () => void;
  onToggleSettings: () => void;
  settingsOpen: boolean;
  filterButtonRef: React.RefObject<HTMLButtonElement | null>;
  settingsButtonRef: React.RefObject<HTMLButtonElement | null>;
}

export function SessionFilterBar({
  activeTab,
  onTabChange,
  counts,
  statementDateChipLabel,
  onToggleFilterPopover,
  filterPopoverOpen,
  onClearChip,
  onClearAllFilters,
  onOpenHowItWorks,
  onPrint,
  onToggleSettings,
  settingsOpen,
  filterButtonRef,
  settingsButtonRef,
}: SessionFilterBarProps) {
  return (
    <div style={wrapStyle}>
      <div style={leftStyle}>
        <button
          ref={filterButtonRef}
          type="button"
          onClick={onToggleFilterPopover}
          style={{ ...iconBtnStyle, ...(filterPopoverOpen ? iconBtnActiveStyle : {}) }}
          aria-label="Open filters"
        >
          <Filter size={16} />
        </button>
        <span style={chipStyle}>
          {statementDateChipLabel}
          <button type="button" onClick={onClearChip} style={chipXStyle} aria-label="Clear chip">&times;</button>
        </span>
        <button type="button" onClick={onClearAllFilters} style={clearStyle}>Clear filter &middot; View all</button>
      </div>

      <div style={rightStyle}>
        <div style={tabsStyle}>
          {(["payments", "deposits", "all"] as TabFilter[]).map((tab) => {
            const isActive = tab === activeTab;
            const label = tab.charAt(0).toUpperCase() + tab.slice(1);
            return (
              <button
                key={tab}
                type="button"
                onClick={() => onTabChange(tab)}
                style={{ ...tabStyle, ...(isActive ? tabActiveStyle : {}) }}
              >
                {label}
                <span style={countStyle}>{counts[tab]}</span>
              </button>
            );
          })}
        </div>
        <button type="button" onClick={onOpenHowItWorks} style={howLinkStyle}>
          <Lightbulb size={14} />
          How it works
        </button>
        <span style={dividerStyle} />
        <button type="button" onClick={onPrint} style={iconBtnStyle} aria-label="Print reconciliation data">
          <Printer size={17} />
        </button>
        <button
          ref={settingsButtonRef}
          type="button"
          onClick={onToggleSettings}
          style={{ ...iconBtnStyle, ...(settingsOpen ? iconBtnActiveStyle : {}) }}
          aria-label="Columns and density"
        >
          <Settings2 size={17} />
        </button>
      </div>
    </div>
  );
}

const wrapStyle: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 32px", borderBottom: `1px solid ${PALETTE.lineSoft}`, background: "#fff" };
const leftStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 14 };
const rightStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 14 };
const iconBtnStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink3, cursor: "pointer", padding: 6, borderRadius: 6, display: "flex", alignItems: "center", fontFamily: "inherit" };
const iconBtnActiveStyle: React.CSSProperties = { background: PALETTE.bgFade, color: PALETTE.ink };
const chipStyle: React.CSSProperties = { background: PALETTE.bgFade, border: `1px solid ${PALETTE.line}`, fontSize: 12.5, color: PALETTE.ink2, fontWeight: 500, padding: "5px 10px 5px 12px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 6 };
const chipXStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink3, cursor: "pointer", fontWeight: 700, padding: 0, fontSize: 14, lineHeight: 1 };
const clearStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.ink3, fontSize: 12.5, fontWeight: 500, cursor: "pointer", padding: 0, fontFamily: "inherit" };
const tabsStyle: React.CSSProperties = { display: "inline-flex", background: PALETTE.bgFade, borderRadius: 10, padding: 3 };
const tabStyle: React.CSSProperties = { padding: "7px 16px", fontSize: 13, color: PALETTE.ink3, fontWeight: 500, cursor: "pointer", borderRadius: 7, background: "transparent", border: 0, fontFamily: "inherit" };
const tabActiveStyle: React.CSSProperties = { background: "#fff", color: PALETTE.ink, fontWeight: 600, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" };
const countStyle: React.CSSProperties = { color: PALETTE.ink4, marginLeft: 4, fontWeight: 500 };
const howLinkStyle: React.CSSProperties = { background: "transparent", border: 0, color: PALETTE.velaDeep, fontSize: 12.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, padding: 4, fontFamily: "inherit" };
const dividerStyle: React.CSSProperties = { width: 1, height: 20, background: PALETTE.line };